import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, Loader2, Car, Home, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const bookingRef = searchParams.get("booking_ref");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);
  const hasStartedPolling = useRef(false);
  const { user } = useAuth();

  const copyBookingRef = () => {
    if (bookingRef) {
      navigator.clipboard.writeText(bookingRef);
      setCopied(true);
      toast.success("Booking reference copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    // Define polling function
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        // For guest users, assume success if we have booking ref
        if (bookingRef && !user) {
          setStatus("success");
        } else {
          setStatus("timeout");
        }
        return;
      }

      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        setPaymentData(response.data);

        if (response.data.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (response.data.status === "expired") {
          setStatus("expired");
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch {
        // For guest users with booking ref, show success
        if (bookingRef && !user) {
          setStatus("success");
        } else {
          setStatus("error");
        }
      }
    };

    pollRef.current = pollPaymentStatus;

    // Start polling only once when sessionId is available
    if (sessionId && !hasStartedPolling.current) {
      hasStartedPolling.current = true;
      pollPaymentStatus();
    } else if (bookingRef && !sessionId) {
      // Guest user returning from Stripe without session tracking
      setStatus("success");
    }
  }, [sessionId, bookingRef, user]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="absolute inset-0 hero-glow opacity-50" />
      
      <Card className="w-full max-w-md bg-card/50 backdrop-blur-md border-black/10 relative z-10">
        <CardContent className="py-12 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold mb-2">Processing Payment</h1>
              <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
              <p className="text-muted-foreground mb-6">
                Thank you for your payment. Your booking has been confirmed.
              </p>
              
              {/* Show booking reference for guests */}
              {bookingRef && (
                <div className="p-4 rounded-lg bg-secondary/30 mb-6">
                  <p className="text-sm text-muted-foreground mb-1">Your Booking Reference</p>
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-xl font-bold font-mono text-primary">{bookingRef}</p>
                    <button 
                      onClick={copyBookingRef}
                      className="p-1 hover:bg-white/10 rounded"
                      title="Copy reference"
                    >
                      {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Save this reference - you'll need it to track your booking
                  </p>
                </div>
              )}
              
              {paymentData && (
                <div className="p-4 rounded-lg bg-secondary/30 mb-6">
                  <p className="text-sm text-muted-foreground">Amount Paid</p>
                  <p className="text-2xl font-bold text-primary">
                    ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </p>
                </div>
              )}
              
              <p className="text-sm text-muted-foreground mb-6">
                A confirmation email with your booking details has been sent to your email address.
              </p>
              
              {user ? (
                <Link to="/dashboard">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8" data-testid="go-to-dashboard">
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <div className="space-y-3">
                  <Link to="/">
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 w-full" data-testid="go-home">
                      <Home className="mr-2 h-4 w-4" />
                      Back to Home
                    </Button>
                  </Link>
                  <Link to="/book">
                    <Button variant="outline" className="rounded-full px-8 w-full">
                      Book Another Trip
                    </Button>
                  </Link>
                </div>
              )}
            </>
          )}

          {status === "timeout" && (
            <>
              <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
                <Car className="h-10 w-10 text-yellow-500" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Payment Pending</h1>
              <p className="text-muted-foreground mb-6">
                Your payment is being processed. You will receive a confirmation email shortly.
              </p>
              {bookingRef && (
                <div className="p-4 rounded-lg bg-secondary/30 mb-6">
                  <p className="text-sm text-muted-foreground">Your Booking Reference</p>
                  <p className="text-xl font-bold font-mono text-primary">{bookingRef}</p>
                </div>
              )}
              {user ? (
                <Link to="/dashboard">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                    Back to Home
                  </Button>
                </Link>
              )}
            </>
          )}

          {(status === "error" || status === "expired") && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <Car className="h-10 w-10 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Payment Failed</h1>
              <p className="text-muted-foreground mb-6">
                There was an issue with your payment. Please try again.
              </p>
              {user ? (
                <Link to="/dashboard">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                    Return to Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/book">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                    Try Again
                  </Button>
                </Link>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
